import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { vehicleModules, VehicleNames, VehicleTypesT } from "../utils/GeneralTypes";
import { getAllVehicleTypeCategoriesAsTree } from "../utils/fetcher";

type Category = {
  id: number;
  /**
   * translated name
   */
  name: string;
  /**
   * link
   */
  icon: string;
  level: number;
  parent_id: number | null;
  children: Category[];
};

/**
 * undefined means didn't fetch yet
 */
export type VehicleTypeCategoriesState = {
  [vehicleType in VehicleNames["single"]]?: {
    vehicleModule: VehicleNames;
    categories?: Category[];
    loading: boolean;
    error?: string | null;
  };
};

const initialState: VehicleTypeCategoriesState = {};

type SetTypeCategoriesPayload = {
  vehicleModule: VehicleNames;
  categories: Category[];
};

export const fetchVehicleTypeCategories = createAsyncThunk(
  "vehicleTypeCategories/fetchByType",
  async (vehicleModule: VehicleNames, { rejectWithValue }) => {
    try {
      const res = await getAllVehicleTypeCategoriesAsTree(vehicleModule.single);
      return { vehicleModule, categories: res.data.categories };
    } catch (err) {
      return rejectWithValue((err as Error).message);
    }
  },
);

const vehicleTypeCategoriesSlice = createSlice({
  name: "vehicleTypeCategories",
  initialState,
  reducers: {
    setTypeCategories: (state, action: PayloadAction<SetTypeCategoriesPayload>) => {
      const { vehicleModule, categories } = action.payload;
      state[vehicleModule.single] = {
        vehicleModule,
        categories,
        loading: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVehicleTypeCategories.pending, (state, action) => {
        state[action.meta.arg.single] = {
          vehicleModule: action.meta.arg,
          loading: true,
          error: null,
        };
      })
      .addCase(fetchVehicleTypeCategories.fulfilled, (state, action) => {
        const { vehicleModule, categories } = action.payload;
        state[vehicleModule.single] = {
          vehicleModule,
          categories,
          loading: false,
          error: null,
        };
      })
      .addCase(fetchVehicleTypeCategories.rejected, (state, action) => {
        // Set error state when fetch fails
        state[action.meta.arg.single] = {
          vehicleModule: action.meta.arg,
          loading: false,
          error: action.payload as string,
        };
      });
  },
});

export const initializeVehicleTypeCategories = () => async (dispatch: any) => {
  // Dispatch all fetch requests in parallel
  await Promise.all(
    vehicleModules.map((module) =>
      dispatch(
        fetchVehicleTypeCategories({
          single: module.single,
          plural: module.plural,
        }),
      ),
    ),
  );
};

export const { setTypeCategories } = vehicleTypeCategoriesSlice.actions;

export const selectAllTypesCategories = (state: RootState) => state.vehicleTypeCategories;

export const selectTypeCategories = (state: RootState, type: VehicleTypesT) =>
  state.vehicleTypeCategories[type]?.categories;

export const selectTypeCategoriesLoading = (state: RootState, type: VehicleTypesT) =>
  state.vehicleTypeCategories[type]?.loading ?? false;

export const selectTypeCategoriesError = (state: RootState, type: VehicleTypesT) =>
  state.vehicleTypeCategories[type]?.error;

export default vehicleTypeCategoriesSlice.reducer;
